import VueRouter, { Route, RouteConfig } from 'vue-router'

export class RouterUtil {
  public static forceAuthFlag = '__force_auth__'

  public static isRoute(hash: string, route: RouteConfig, router?: VueRouter) {
    if (hash) {
      let path = hash

      const queryPos = hash.indexOf('?')
      if (queryPos !== -1) {
        path = hash.slice(0, queryPos)
      }

      return path === this.buildPathFromRoute(route, router)
    }

    return false
  }

  public static isCurRoute(route: RouteConfig, router?: VueRouter) {
    return this.isRoute(location.hash, route, router)
  }

  public static isUrlRoute(url: string, route: RouteConfig, router?: VueRouter) {
    return this.isRoute(this.getHashFromUrl(url), route, router)
  }

  public static isForceAuth(_route: Route) {
    return !!(_route.query && this.forceAuthFlag in _route.query)
  }

  public static forceAuth(route: Route, router: VueRouter) {
    return router.replace({ path: route.path, query: { ...route.query, [this.forceAuthFlag]: null } })
  }

  public static unforceAuth(route: Route, router: VueRouter) {
    return router.replace({ path: route.path, query: this.removeForceAuthFlagFromRoute(route).query })
  }

  public static getHashFromUrl(url: string) {
    const a = document.createElement('a')

    a.href = url

    return a.hash
  }

  public static buildQuery(url: string) {
    return new URL(url.replace(/#\//, '')).searchParams
  }

  public static buildQueryStr(query: { [index: string]: any }) {
    let queryStr = ''

    for (const key in query) {
      if (queryStr) {
        queryStr += '&' + key + '=' + query[key]
      } else {
        queryStr += '?' + key + '=' + query[key]
      }
    }

    return queryStr
  }

  public static buildQueryObj(url: string) {
    const result: Record<string, string> = {}
    const query = this.buildQuery(url)

    for (const e of query.entries()) {
      result[e[0]] = e[1]
    }

    return result
  }

  public static appendQuery(_url: string, _query: Record<string, string>) {
    const query = this.buildQuery(_url)
    for (const key of Object.keys(_query)) {
      query.append(key, _query[key])
    }

    let url = _url
    const questionMarkIndex = _url.indexOf('?')
    if (questionMarkIndex !== -1) {
      url = _url.substring(0, questionMarkIndex)
    }

    return `${url}?${query.toString()}`
  }

  public static buildUrlFromRoute(
    route: { name?: string | null; path?: string | null },
    query: { [index: string]: string | undefined | null | (string | null)[] } | null = null,
    router?: VueRouter
  ) {
    const url = `${location.origin}${location.pathname}${this.buildPathFromRoute(route, router)}`

    if (query) {
      return `${url}${this.buildQueryStr(query)}`
    }

    return url
  }

  public static buildPathFromRoute(route: { name?: string | null; path?: string | null }, router?: VueRouter) {
    return router && route.name ? router.resolve({ name: route.name }).href : `#${route.path}`
  }

  public static removeForceAuthFlagFromUrl(_url: string) {
    const url = new URL(_url)

    url.searchParams.delete(this.forceAuthFlag)

    return url.href
  }

  public static removeForceAuthFlagFromRoute(_route: Route) {
    const route: Route = { ..._route, query: { ..._route.query } }

    delete route.query[this.forceAuthFlag]

    return route
  }

  public static cloneRoutes(
    routes: RouteConfig[],
    prefix: string,
    title: string,
    props: { [index: string]: string },
    roles?: string[]
  ) {
    const result: RouteConfig[] = []

    for (const route of routes) {
      result.push({
        ...route,
        path: RouterUtil.cloneRoutePath(route.path, prefix),
        name: `${prefix}_${route.name}`,
        meta: {
          ...(route.meta || {}),
          roles: roles ? [...roles] : route.meta?.roles,
          title,
          messageTarget: '',
        },
        props: (_route) => {
          if (typeof route.props === 'function') {
            return {
              ...route.props(_route),
              ...props,
            }
          }

          return props
        },
        redirect: route.redirect
          ? RouterUtil.cloneRouteRedirect(route.redirect as RouteConfig, prefix, title, props)
          : route.redirect,
        children: route.children ? RouterUtil.cloneRoutes(route.children, prefix, title, props, roles) : undefined,
      })
    }

    return result
  }

  public static cloneRoutePath(_path: string, _prefix: string) {
    if (_path.startsWith('/')) {
      const path = [..._path]

      path.splice(_path.lastIndexOf('/') + 1, 0, `${_prefix}-`)

      return path.join('')
    }

    return _path
  }

  public static cloneRouteRedirect(
    _redirect: RouteConfig | string,
    prefix: string,
    title: string,
    props: { [index: string]: string }
  ) {
    if (typeof _redirect === 'string') {
      return `${prefix}-${_redirect}`
    } else {
      return RouterUtil.cloneRoutes([_redirect], prefix, title, props)[0]
    }
  }
}
